'use client'

import React, { FunctionComponent, useCallback } from 'react'
import classNames from 'classnames'
import { getPromoCodes, mockApplyGiftCards } from '@/utils/cart.utils'
import useGetTotalWithDiscount from '@/hooks/useGetTotalWithDiscount'
import { getSubtotalDisplayFlags } from '@/utils/components/SubtotalsList.utils'
import { toCurrency } from '@/utils/currency.utils'
import TXT from '@/messages'
// styles
import localStyles from './SubtotalsList.module.css'
import lineItemsStyles from '@/styles/templates/LineItems.module.css'
import orderSummaryStyles from '@/app/_components/OrderSummary/OrderSummary.module.css'
import detailsListStyles from '@/app/_components/OrderSummary/DetailsList.module.css'
import TaxLineItemLabel from '../TaxLineItemLabel/TaxLineItemLabel'
import { useSeller } from '@/app/_providers/SellerProvider/SellerProvider'

type Props = {
  cart: Transformed.Cart
  giftCardBalance: readonly Transformed.GiftCardBalance[]
  isOrderConfirmation?: boolean
  onRemoveGiftCard?: (id: string) => void
  localLineItems?: Transformed.LineItem[]
  lineItemsToDelete?: Transformed.LineItem[]
}

const SubtotalsList: FunctionComponent<Props> = (props: Props) => {
  const {
    cart,
    giftCardBalance,
    isOrderConfirmation = false,
    onRemoveGiftCard,
    localLineItems = [],
    lineItemsToDelete = [],
  } = props
  /** The tax to display */
  const tax = cart.taxesTotal
  /** The fee to display */
  const fees = cart.feesTotal

  const {
    discount,
    subTotal,
    total,
    giftCardAppliedAmount: giftCardsApplied,
  } = useGetTotalWithDiscount({
    cart,
    giftCardBalance,
    localLineItems,
    lineItemsToDelete,
  })

  const { processingFeeOverride } = useSeller()

  const promoCodes = getPromoCodes(cart)
  const {
    appliedAmount,
    balances: giftCardBalances,
  } = mockApplyGiftCards(cart, giftCardBalance as Transformed.GiftCardBalance[] | null)

  const giftCardAppliedAmount = isOrderConfirmation
    ? giftCardsApplied
    : appliedAmount

  const handleRemoveGiftCard = useCallback((id: string): void => {
    onRemoveGiftCard?.(id)
  }, [onRemoveGiftCard])

  const placeThirdClass = { [localStyles.PlaceThird]: isOrderConfirmation }

  const {
    isSubtotal,
    isDiscount,
    isFees,
    isTax,
    isGiftCardApplied,
    showAdditionalSeparator,
  } = getSubtotalDisplayFlags(subTotal, discount, fees, tax, giftCardBalance, giftCardAppliedAmount)

  return (
    <div>
      {isSubtotal && (
        <div className={classNames('subtotal', localStyles.SubTotalItem)}>
          <h2 className={classNames(
            orderSummaryStyles.GeneralTitle,
            orderSummaryStyles.MinorTitle,
          )}
          >
            {TXT.subtotalsList.subtotal}
          </h2>
          <span className={classNames(localStyles.Price, placeThirdClass)} data-testid="order-subtotal">
            {toCurrency(subTotal, 2)}
          </span>
          <span className={localStyles.Placeholder} />
        </div>
      )}

      {/** TODO switch to cartMod */}
      <div aria-live="polite" aria-atomic="true" className={classNames(localStyles.DiscountWrapper)}>
        {isDiscount && (
          <div className={classNames(localStyles.SubTotalItem, localStyles.Discount)}>
            <div>
              <h2
                className={classNames(
                  orderSummaryStyles.Discount,
                  orderSummaryStyles.GeneralTitle,
                  orderSummaryStyles.MinorTitle,
                )}
                data-testid="discount-applied"
              >
                {TXT.cartSummary.discounts(promoCodes.length)}
                {' '}
                <span className={localStyles.Applied}>{TXT.orderSummary.applied}</span>
              </h2>
            </div>
            <span
              className={classNames(localStyles.Price, localStyles.Discount, placeThirdClass)}
            />
            <span className={localStyles.Placeholder} />
            <ul
              className={classNames(
                detailsListStyles.ListMain,
                'details-list',
              )}
              data-testid="discount-cart-mods"
            >
              {promoCodes.map((promoCode) => promoCode.discountAmount > 0 && (
                <li
                  className={classNames(
                    detailsListStyles.ListItem,
                    detailsListStyles.Discount,
                  )}
                  key={promoCode.id}
                >
                  <span>{promoCode.promoCode}</span>
                  <span
                    className={classNames(localStyles.Discount, placeThirdClass)}
                    data-testid="discount-amount"
                  >
                    -
                    {toCurrency(promoCode.discountAmount)}
                  </span>
                  <span className={localStyles.Placeholder} />
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {isFees && (
        <div className={classNames('fees', localStyles.SubTotalItem)}>
          <h2 className={classNames(
            orderSummaryStyles.GeneralTitle,
            orderSummaryStyles.MinorTitle,
          )}
          >
            {processingFeeOverride || TXT.subtotalsList.processingFee}
          </h2>
          <span
            className={classNames(localStyles.Price, placeThirdClass)}
            data-testid="order-fee"
          >
            {toCurrency(fees, 2)}
          </span>
          <span className={localStyles.Placeholder} />
        </div>
      )}

      {isTax && (
        <div className={classNames('tax', localStyles.SubTotalItem)}>
          <TaxLineItemLabel
            label={TXT.subtotalsList.tax}
          />
          <span
            className={classNames(localStyles.Price, placeThirdClass)}
            data-testid="order-tax"
          >
            {subTotal > 0 || (subTotal <= 0 && discount > 0)
              ? toCurrency(tax, 2)
              : '-'}
          </span>
          <span className={localStyles.Placeholder} />
        </div>
      )}

      {isGiftCardApplied && (
        <div className={classNames('gift-card', localStyles.SubTotalItem)}>
          <div>
            <h2 className={classNames(
              orderSummaryStyles.GeneralTitle,
              orderSummaryStyles.MinorTitle,
            )}
            >
              {TXT.cartSummary.giftCards(giftCardBalances.length)}
            </h2>
            {!isOrderConfirmation && (
              <ul
                className={classNames(
                  'details-list',
                  detailsListStyles.ListMain,
                  detailsListStyles.GiftCards,
                )}
                data-testid="discount-cart-mods"
              >
                {giftCardBalances.map((balance: Transformed.AppliedGiftCardBalance) => (
                  <li className={detailsListStyles.ListItem} key={balance.id}>
                    <strong>{balance.id}</strong>
                    <br />
                    {TXT.subtotalsList.remainingBalance}
                    {' '}
                    {toCurrency(balance.remaining, 2)}
                    {!isOrderConfirmation && onRemoveGiftCard && (
                      <button
                        className={classNames(
                          'btn-unstyled',
                          lineItemsStyles.RemoveLink,
                        )}
                        onClick={() => handleRemoveGiftCard(balance.id)}
                        type="button"
                      >
                        {TXT.subtotalsList.remove}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <span
            className={classNames(localStyles.Price, localStyles.Discount, placeThirdClass)}
            data-testid="discount-amount"
          >
            -
            {toCurrency(giftCardAppliedAmount, 2)}
          </span>
          <span className={localStyles.Placeholder} />
        </div>
      )}

      {showAdditionalSeparator && (
        <hr className="separator dark" />
      )}

      <div
        className={classNames(localStyles.Total, localStyles.SubTotalItem)}
        data-testid="order-total-container"
      >
        <h2 className={classNames(
          localStyles.Title,
          orderSummaryStyles.GeneralTitle,
        )}
        >
          {TXT.subtotalsList.total}
        </h2>
        <span
          className={classNames(localStyles.Price, placeThirdClass)}
          data-testid="order-total"
        >
          {subTotal >= 0 ? toCurrency(total - giftCardAppliedAmount, 2) : '-'}
        </span>
        <span className={localStyles.Placeholder} />
      </div>
    </div>
  )
}

export default SubtotalsList
